import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Lock, MapPin } from "lucide-react";
import { fetchPublicShare } from "@/api";
import { categoryLabel, formatPrice } from "@/lib/utils";

const CATEGORIES = [
  { value: "all", label: "All" },
  { value: "inspiration", label: "Inspiration" },
  { value: "site", label: "Site" },
  { value: "product", label: "Product" },
  { value: "material", label: "Material" },
  { value: "floorplan", label: "Floorplan" },
];

export default function PublicProjectView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const res = await fetchPublicShare(token);
        if (active) setData(res);
      } catch (e) {
        if (active) setError("This link is invalid or has been revoked.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF8F4] text-stone-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading shared project…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FAF8F4] px-6 text-center">
        <div className="w-14 h-14 rounded-full bg-stone-200 flex items-center justify-center mb-5">
          <Lock className="w-6 h-6 text-stone-500" strokeWidth={1.5} />
        </div>
        <h1 className="font-display text-3xl text-stone-900 mb-2">Link unavailable</h1>
        <p className="text-stone-500 max-w-md">
          {error || "This share link is invalid or has been revoked."}
        </p>
      </div>
    );
  }

  if (data.type === "moodboard") {
    return <PublicMoodboard data={data} />;
  }
  return <PublicProject data={data} filter={filter} setFilter={setFilter} />;
}

function PublicHeader({ project }) {
  return (
    <header className="border-b border-stone-200 bg-white/70 backdrop-blur">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span
            className="w-2 h-2 rounded-full"
            style={{ background: project?.color_tag || "#8A9A86" }}
          />
          <span className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold">
            {project?.client || "Atelier Studio"}
          </span>
        </div>
        <span className="text-xs uppercase tracking-[0.2em] text-stone-400">
          Read-only preview
        </span>
      </div>
    </header>
  );
}

function PublicProject({ data, filter, setFilter }) {
  const { project, images = [], moodboards = [], floorplans = [], products = [] } = data;
  const filteredImages =
    filter === "all" ? images : images.filter((i) => i.category === filter);

  return (
    <div className="min-h-screen bg-[#FAF8F4]" data-testid="public-project-view">
      <PublicHeader project={project} />
      <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-12 md:py-16">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-14"
        >
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-stone-900">
            {project.name}
          </h1>
          {project.description && (
            <p className="text-stone-500 mt-3 max-w-2xl text-base">
              {project.description}
            </p>
          )}
        </motion.div>

        {/* Library */}
        {images.length > 0 && (
          <Section title="Library" count={images.length}>
            <div className="flex flex-wrap gap-2 mb-6">
              {CATEGORIES.map((c) => (
                <button
                  key={c.value}
                  onClick={() => setFilter(c.value)}
                  data-testid={`public-filter-${c.value}`}
                  className={`px-4 py-1.5 rounded-full text-sm transition-colors ${
                    filter === c.value
                      ? "bg-stone-900 text-white"
                      : "bg-white text-stone-700 hover:bg-stone-100 border border-stone-200"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
            <div
              className="columns-2 md:columns-3 lg:columns-4 gap-4"
              data-testid="public-image-masonry"
            >
              {filteredImages.map((img, idx) => (
                <motion.div
                  key={img.id}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.015 }}
                  className="mb-4 break-inside-avoid rounded-xl overflow-hidden bg-white border border-stone-200/70"
                >
                  <img
                    src={img.data}
                    alt={img.title || img.filename || ""}
                    className="w-full h-auto object-cover"
                  />
                  {(img.title || img.tags?.length) && (
                    <div className="px-3 py-2">
                      {img.title && (
                        <p className="text-sm text-stone-900 truncate">{img.title}</p>
                      )}
                      {img.tags?.length > 0 && (
                        <p className="text-[10px] uppercase tracking-wider text-stone-500 mt-0.5">
                          {img.tags.slice(0, 4).join(" · ")}
                        </p>
                      )}
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </Section>
        )}

        {/* Moodboards */}
        {moodboards.length > 0 && (
          <Section title="Moodboards" count={moodboards.length}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {moodboards.map((mb) => (
                <div
                  key={mb.id}
                  data-testid={`public-moodboard-${mb.id}`}
                  className="aspect-[4/3] relative rounded-2xl overflow-hidden bg-[#F7F7F5] border border-stone-200"
                >
                  <MoodboardThumb mb={mb} images={images} />
                  <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-white via-white/85 to-transparent">
                    <h4 className="font-display text-lg text-stone-900">{mb.name}</h4>
                    <p className="text-xs text-stone-500">
                      {mb.items?.length || 0} item
                      {mb.items?.length === 1 ? "" : "s"}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Floorplans */}
        {floorplans.length > 0 && (
          <Section title="Floorplans" count={floorplans.length}>
            <div className="space-y-10">
              {floorplans.map((fp) => {
                const img = images.find((i) => i.id === fp.image_id);
                if (!img) return null;
                return (
                  <div
                    key={fp.id}
                    data-testid={`public-floorplan-${fp.id}`}
                    className="bg-white border border-stone-200 rounded-2xl overflow-hidden"
                  >
                    <div className="relative">
                      <img src={img.data} alt="" className="w-full h-auto object-contain" />
                      {(fp.pins || []).map((p, idx) => (
                        <div
                          key={idx}
                          className="absolute -translate-x-1/2 -translate-y-full"
                          style={{ left: `${(p.x || 0) * 100}%`, top: `${(p.y || 0) * 100}%` }}
                        >
                          <div className="bg-stone-900 text-white rounded-full px-2.5 py-0.5 text-xs font-semibold flex items-center gap-1 shadow">
                            <MapPin className="w-3 h-3" strokeWidth={2} />
                            {idx + 1}
                          </div>
                        </div>
                      ))}
                    </div>
                    {fp.pins?.length > 0 && (
                      <div className="px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-stone-700">
                        {fp.pins.map((p, idx) => (
                          <div key={idx}>
                            <span className="font-semibold text-stone-900">{idx + 1}.</span>{" "}
                            {p.note || p.label || "(no note)"}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Section>
        )}

        {/* Products */}
        {products.length > 0 && (
          <Section title="Products" count={products.length}>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {products.map((p) => {
                const img = images.find((x) => x.id === p.image_id);
                return (
                  <div key={p.id} data-testid={`public-product-${p.id}`}>
                    <div className="aspect-square overflow-hidden rounded-xl bg-stone-100">
                      {img ? (
                        <img
                          src={img.data}
                          alt={p.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-stone-400 text-xs">
                          {categoryLabel("product")}
                        </div>
                      )}
                    </div>
                    <div className="mt-3 flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-medium text-stone-900 truncate">{p.name}</p>
                        {p.supplier && (
                          <p className="text-xs text-stone-500 truncate">{p.supplier}</p>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm text-stone-900">
                          {formatPrice(p.price, p.currency)}
                        </p>
                        <p className="text-[10px] uppercase tracking-wider text-stone-500">
                          {p.status}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Section>
        )}

        {images.length === 0 &&
          moodboards.length === 0 &&
          floorplans.length === 0 &&
          products.length === 0 && (
            <div className="border border-dashed border-stone-200 rounded-2xl py-20 text-center text-stone-500">
              Nothing has been shared on this project yet.
            </div>
          )}

        <footer className="mt-20 pt-6 border-t border-stone-200 text-xs text-stone-400 tracking-wider uppercase text-center">
          Shared with Atelier Studio · Read-only
        </footer>
      </div>
    </div>
  );
}

function MoodboardThumb({ mb, images }) {
  const byId = Object.fromEntries(images.map((i) => [i.id, i]));
  const items = [...(mb.items || [])].sort((a, b) => (a.z || 0) - (b.z || 0));
  const CANVAS_W = 1600;
  const CANVAS_H = 1000;
  const FONT_FAMILIES = {
    display: "'PP Editorial Old', 'Cormorant Garamond', Georgia, serif",
    sans: "'Inter', system-ui, sans-serif",
    mono: "'JetBrains Mono', ui-monospace, monospace",
  };
  if (items.length === 0) {
    return (
      <div className="absolute inset-0 flex items-center justify-center text-stone-400 text-sm">
        Empty board
      </div>
    );
  }
  return (
    <div className="absolute inset-0">
      {items.map((it, idx) => {
        const left = (it.x / CANVAS_W) * 100;
        const top = (it.y / CANVAS_H) * 100;
        const w = (it.w / CANVAS_W) * 100;
        const h = (it.h / CANVAS_H) * 100;
        const baseStyle = {
          left: `${left}%`,
          top: `${top}%`,
          width: `${w}%`,
          height: `${h}%`,
          transform: `rotate(${it.rotation || 0}deg)`,
        };
        if (it.kind === "text") {
          return (
            <div
              key={idx}
              className="absolute flex items-center px-2 leading-tight whitespace-pre-wrap break-words"
              style={{
                ...baseStyle,
                fontFamily: FONT_FAMILIES[it.font_family] || FONT_FAMILIES.display,
                fontSize: `${(it.font_size || 32) * 0.6}px`,
                color: it.color || "#1c1b17",
                textAlign: it.align || "left",
                justifyContent:
                  it.align === "center"
                    ? "center"
                    : it.align === "right"
                    ? "flex-end"
                    : "flex-start",
              }}
            >
              {it.text || ""}
            </div>
          );
        }
        const img = byId[it.image_id];
        if (!img) return null;
        const isPdfItem =
          (img.mime || "").toLowerCase() === "application/pdf" ||
          /^data:application\/pdf/.test(img.data || "") ||
          (img.filename || "").toLowerCase().endsWith(".pdf");
        if (isPdfItem) {
          return (
            <div
              key={idx}
              className="absolute bg-white rounded-sm shadow flex flex-col items-center justify-center text-stone-700"
              style={baseStyle}
            >
              <div className="text-[8px] uppercase tracking-[0.18em] font-semibold opacity-70">PDF</div>
              <div className="text-[10px] truncate max-w-[80%]">{img.filename || "Document"}</div>
            </div>
          );
        }
        return (
          <img
            key={idx}
            src={img.data}
            alt=""
            className="absolute object-cover rounded-sm shadow"
            style={baseStyle}
          />
        );
      })}
    </div>
  );
}

function PublicMoodboard({ data }) {
  const { moodboard, project, images = [] } = data;
  return (
    <div className="min-h-screen bg-[#FAF8F4]" data-testid="public-moodboard-view">
      <PublicHeader project={project} />
      <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-12">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <p className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold mb-1">
            Moodboard
          </p>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight text-stone-900">
            {moodboard.name}
          </h1>
        </motion.div>
        <div
          className="relative w-full rounded-2xl overflow-hidden bg-white border border-stone-200 shadow-sm"
          style={{ aspectRatio: "16 / 10", background: moodboard.background || "#F7F7F5" }}
        >
          <MoodboardThumb mb={moodboard} images={images} />
        </div>
        <footer className="mt-12 pt-6 border-t border-stone-200 text-xs text-stone-400 tracking-wider uppercase text-center">
          Shared with Atelier Studio · Read-only
        </footer>
      </div>
    </div>
  );
}

function Section({ title, count, children }) {
  return (
    <section className="mb-16">
      <div className="flex items-end justify-between mb-6">
        <h2 className="font-display text-3xl tracking-tight text-stone-900">{title}</h2>
        <span className="text-xs uppercase tracking-[0.2em] text-stone-400">
          {count} {count === 1 ? "item" : "items"}
        </span>
      </div>
      {children}
    </section>
  );
}
